// Hiring board and team roster.
import { STAFF, TASK, TASK_IDS, type TaskId } from '../config/constants';
import * as Staff from '../core/Staff';
import type { Employee, StaffStation } from '../core/GameState';
import type { RenderResult } from './Panel';
import { $, fmt } from './panels';
import type { UI } from './UI';
import { mouseArt } from '../art/worldArt';

function pips(v: number): string {
  return `<span class="stat-pips">${Array.from({ length: 10 }, (_, i) => `<span class="${i < v ? 'on' : ''}"></span>`).join('')}</span>`;
}

function statsHtml(e: Employee): string {
  const s = e.stats;
  return `<div class="muted">Intelligence ${pips(s.intelligence)} · Work ethic ${pips(s.workEthic)}<br>Loyalty ${pips(s.loyalty)} · Hygiene ${pips(s.hygiene)}</div>`;
}

function skillsHtml(e: Employee): string {
  const top = TASK_IDS.filter((t) => Staff.totalXp(e, t) > 0)
    .sort((a, b) => Staff.totalXp(e, b) - Staff.totalXp(e, a))
    .slice(0, 5)
    .map((t) => `${TASK[t].label} L${Staff.skillLevel(e, t)}${Staff.isMaster(e, t) ? '★' : ''}`);
  return `<div class="muted">Skills: ${top.length ? top.join(' · ') : 'none yet'}</div>`;
}

export function openStaffPanel(ui: UI): void {
  const game = ui.game;
  ui.panels.open({
    id: 'staff',
    title: 'Staff',
    kind: 'menu',
    side: 'center',
    render(body, api): RenderResult {
      if (!game.hasStaff) {
        body.innerHTML = `<div class="muted">Unlock <b>Employees</b> after the Cellar, then personally make, age, and sell a cheese before hiring. Employees work in real time, carry output between stations, learn from your training bank once you master a process, and can be promoted to trainers.</div>`;
        return {};
      }
      body.innerHTML = `
        <div class="stat"><span>Gold</span><b class="gold accent"></b></div>
        <div class="muted">Wages are paid at day end for days worked. Pay below what experience commands and people miss days or quit. Intelligence sets how fast they learn and how rarely they slip. Work ethic sets how much they get done. Hygiene moves cheese quality up or down.</div>
        <div class="section"><h4>Team</h4>
          <div class="row"><span class="muted">Creamery worker makes:</span><select class="sel recipe"></select></div>
          <div class="staff-grid team"></div></div>
        <div class="section"><h4>Hiring board <span class="muted">(new faces every ${STAFF.CANDIDATE_REFRESH_DAYS} days)</span></h4><div class="staff-grid cands"></div></div>`;
      const coins = $(body, '.gold');
      const recipe = $<HTMLSelectElement>(body, '.recipe');
      const team = $(body, '.team');
      const cands = $(body, '.cands');

      const options: [string | null, string][] = [[null, 'Leave my milk alone']];
      for (const d of game.availableRecipes()) options.push([d.id, d.name]);
      for (const [val, label] of options) {
        const o = document.createElement('option');
        o.value = val ?? '';
        o.textContent = label;
        recipe.appendChild(o);
      }
      recipe.value = game.state.staff.vatRecipe ?? '';
      recipe.onchange = () => game.setVatRecipe(recipe.value || null);

      const build = (): void => {
        const s = game.state;
        team.innerHTML = s.staff.employees.length ? '' : '<div class="muted">Nobody on the payroll yet.</div>';
        for (const e of s.staff.employees) {
          const card = document.createElement('div');
          card.className = `staff-card ${e.presentToday ? '' : 'absent'}`;
          const expected = Staff.expectedWage(e);
          const mismatch = Staff.payMismatch(e);
          card.innerHTML = `
            <div class="staff-portrait">${mouseArt(e.station ?? 'player', 's')}</div>
            <div class="title">${e.name}${e.trainerFor ? ` <span class="accent">· ${TASK[e.trainerFor].label} trainer</span>` : ''}${e.presentToday ? '' : ' <span class="bad">· not coming tomorrow</span>'}</div>
            ${statsHtml(e)}
            ${skillsHtml(e)}
            <div class="muted">Job: ${e.jobLabel || 'Idle'}${e.jobStatus === 'waiting' ? ` <span class="bad">(${e.blockedReason ?? 'waiting'})</span>` : ''} · progress ${Math.round((e.jobProgress ?? 0) * 100)}%</div>
            <div class="muted">Wage ${fmt(e.wage)} / day · experience commands ${fmt(expected)}${mismatch > 0.1 ? ` <span class="bad">(underpaid ${Math.round(mismatch * 100)}%)</span>` : ''} · attendance ${Math.round(Staff.attendanceChance(e) * 100)}% · worked ${e.daysWorked}, missed ${e.daysMissed}</div>
            <div class="row">
              <span class="muted">Station</span><select class="sel station"></select>
              <span class="muted">Wage</span><input class="num wage" type="number" min="1" step="0.5" value="${e.wage}">
              <button class="btn small match">Match ${fmt(expected)}</button>
            </div>
            <div class="row promote"></div>
            <div class="row"><button class="btn small danger fire">Dismiss</button></div>`;
          const station = $<HTMLSelectElement>(card, '.station');
          for (const [val, label] of [['', 'Unassigned'], ['barn', Staff.STATION_LABEL.barn], ['vat', Staff.STATION_LABEL.vat], ['cellar', Staff.STATION_LABEL.cellar]] as [string, string][]) {
            const o = document.createElement('option');
            o.value = val;
            o.textContent = label;
            station.appendChild(o);
          }
          station.value = e.station ?? '';
          station.onchange = () => game.assign(e.id, (station.value || null) as StaffStation | null);
          const wage = $<HTMLInputElement>(card, '.wage');
          wage.onchange = () => game.setWage(e.id, Number(wage.value));
          $<HTMLButtonElement>(card, '.match').onclick = () => {
            game.setWage(e.id, expected);
            api.rerender();
          };
          const promote = $(card, '.promote');
          const masters = TASK_IDS.filter((t) => Staff.isMaster(e, t));
          if (!e.trainerFor && masters.length) {
            promote.innerHTML = '<span class="muted">Promote to trainer:</span>';
            for (const t of masters as TaskId[]) {
              const b = document.createElement('button');
              b.className = 'btn small primary';
              b.textContent = TASK[t].label;
              const block = Staff.canPromote(s, e, t);
              b.disabled = !!block;
              b.title = block ?? `Wage ×${STAFF.TRAINER_WAGE_MULT}; trains every junior in ${TASK[t].label.toLowerCase()} 1% a day`;
              b.onclick = () => {
                if (game.promote(e.id, t)) api.rerender();
              };
              promote.appendChild(b);
            }
          }
          $<HTMLButtonElement>(card, '.fire').onclick = () => {
            if (confirm(`Dismiss ${e.name}?`)) {
              game.dismiss(e.id);
              api.rerender();
            }
          };
          team.appendChild(card);
        }
        cands.innerHTML = '';
        for (const c of game.candidates()) {
          const card = document.createElement('div');
          card.className = 'staff-card';
          card.innerHTML = `
            <div class="staff-portrait">${mouseArt(c.station ?? 'player', 's')}</div>
            <div class="title">${c.name}</div>
            ${statsHtml(c)}
            ${skillsHtml(c)}
            <div class="muted">Asks ${fmt(c.wage)} coins / day · signing fee ${fmt(Staff.signingFee(c))} (${STAFF.SIGNING_FEE_DAYS} days' wages)</div>
            <div class="row"><button class="btn small primary hire">Hire ${c.name}</button></div>`;
          $<HTMLButtonElement>(card, '.hire').onclick = () => {
            if (game.hire(c.id)) api.rerender();
          };
          cands.appendChild(card);
        }
        if (!cands.children.length) cands.innerHTML = '<div class="muted">Nobody is looking for work right now.</div>';
      };
      build();
      const update = (): void => {
        coins.textContent = fmt(game.state.gold);
      };
      update();
      return { update };
    },
  });
}
